/**
 * Helper: Format
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 * - Add in alphabetical order.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@shopify/theme-currency'

import { convertToHandle } from '~/helpers/convert'
import { isCdnUrl } from '~/helpers/general'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Formats response from product.
 * - Any updates here must be reflected in formatBoostProduct() and
 * formatNostoProduct() in their corresponding files.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatPrice(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    media: formatMedia(product),
    options: formatOptions(product),
    price: formatPrice(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.productType,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.variants = formatVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  /**
   * Product badges.
   */
  formattedProduct.badges = formattedProduct.tags.map((tag) => {
    if (!tag.includes('Badge:')) {
      return false
    }

    return tag.split('Badge:')[1].trim()
  }).filter(Boolean)

  /**
   * Format metafields.
   * - Add each metafield to object, then add case.
   */
  const metafields = {
    colours: false,
    coming_soon: false,
    custom_id: '',
    vendor_link: '',
    product_title: product.title,
  }

  Object.keys(metafields).forEach((key) => {
    const object = product.metafields?.find((metafield) => {
      return metafield?.key === key
    })

    if (!object) {
      return
    }

    let value = object.value
    let formattedKey = key

    switch (key) {
      case 'colours':
        value = JSON.parse(object.value)
        break

      case 'coming_soon':
        formattedKey = 'isComingSoon'
        value = JSON.parse(Boolean(object.value))
        delete metafields[key]
        break

      case 'custom_id':
        formattedKey = 'customId'
        delete metafields[key]
        break

      case 'product_title':
        value = object.value
        break

      case 'vendor_link':
        value = value.replace(
          'https://childsplayredirect.com/',
          `//${location.host}${cnvs.routes.normalisedRoot}`,
        )

        formattedKey = 'vendorLink'
        delete metafields[key]
        break
    }

    metafields[formattedKey] = value
  })

  return {
    ...formattedProduct,
    ...metafields,
  }
}

/**
 * Formats price.
 * @param {Boolean} loading - Loading state.
 * @param {Object} price - Price response.
 * @returns {Object}
 */
export function formatPrice(price, loading = false) {
  return {
    amount: Number(price?.minVariantPrice.amount) * 100 ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    loading,
    max: Number(price?.maxVariantPrice.amount) * 100 ?? false,
    min: Number(price?.minVariantPrice.amount) * 100 ?? false,
    varies: price?.maxVariantPrice.amount !== price?.minVariantPrice.amount,
  }
}

/**
 * Formats images.
 * @param {Object} product - GraphQL response.
 * @param {Array} images - Direct array of images, for formatting single images.
 * @returns {Array}
 */
function formatImages(product, images) {
  const imagesArray = images ? images : transformEdges(product.images)

  const formattedImages = imagesArray.map((image) => {
    if (!image.url) {
      return {}
    }

    const linkedVariant = product?.variants?.length
      ? transformEdges(product?.variants).find((variant) => {
        return variant.image.id === image.id
      })
      : false

    return {
      alt: image.altText,
      height: image.height,
      id: decodeApiId(image.id),
      src: image.url,
      variant: decodeApiId(linkedVariant?.id) || false,
      width: image.width,
    }
  })

  if (images) {
    return formattedImages[0]
  }

  return formattedImages
}

/**
 * Formats media.
 * @param {Oject} product - GraphQL response.
 * @returns {Array}
 */
function formatMedia(product) {
  return transformEdges(product.media).map((media, index) => {
    const formattedMedia = {
      alt: media.alt,
      id: decodeApiId(media.id) || false,
      mediaType: media.mediaType.toLowerCase(),
      position: index + 1,
    }

    switch (formattedMedia.mediaType) {
      case 'external_video':
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.host = media.host
        formattedMedia.embedUrl = media.embedUrl
        formattedMedia.previewImage = formatImages(false, [media.previewImage])
        break

      case 'image':
        formattedMedia.image = formatImages(false, [media.image])
        break

      case 'model':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            mimeType: source.mimeType,
            url: source.url,
          }
        })
        break

      case 'video':
        formattedMedia.previewImage = formatImages(false, [media.previewImage])

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            height: source.height,
            mimeType: source.mimeType,
            url: source.url,
            width: source.width,
          }
        })
        break
    }

    return formattedMedia
  })
}

/**
 * Formats options.
 * @param {Object} product - GraphQL response.
 * @returns {Array}
 */
function formatOptions(product) {
  return product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })
}

/**
 * Formats variants.
 * @param {Object} product - GraphQL response.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: Number(variant.compareAtPrice?.amount) * 100 ?? false,
        currencyCode: variant.compareAtPrice?.currencyCode ?? false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) ?? false,
      inventory: variant.quantityAvailable,
      price: {
        amount: Number(variant.price.amount) * 100 ?? false,
        currencyCode: variant.price.currencyCode ?? false,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.title

    return formattedVariant
  })
}

/**
 * Formats response from collection.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatCollection(response) {
  const collection = response.collection

  const formattedCollection = {
    description: collection.descriptionHtml,
    handle: collection.handle,
    id: decodeApiId(collection.id),
    pageInfo: {
      ...collection.products?.pageInfo,
      currentPage: 1,
    },
    title: collection.title,
    url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}`,
  }

  /**
   * Format products.
   */
  formattedCollection.products = transformEdges(collection.products).map((productResponse) => {
    const product = productResponse

    return {
      available: product.available,
      compareAtPrice: formatPrice(product.compareAtPriceRange),
      featuredImage: formatImages(product)[0],
      handle: product.handle,
      price: formatPrice(product.priceRange),
      tags: product.tags,
      title: product.title,
      type: product.productType,
      url: `//${location.host}${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}`,
    }
  })

  return formattedCollection
}

/**
 * Structures the payload data for Klaviyo.
 *
 * @param {FormData} data - input data from form.
 * @returns {Object}
 */
export function formatKlaviyoData(data) {
  return {
    list_id: data.get('g'),
    custom_source: data.get('$source'),
    email: data.get('email'),
  }
}

/**
 * Formats response from menu.
 * @param {String} [prefix] - Prefix to prepend to handle to make it unique.
 * @param {String} [previousHandle] - Previous tier's handle.
 * @param {Object} response - GraphQL response or nested items array.
 * @param {Number} [tier] - Current tier number.
 * @returns {Array}
 */
export function formatMenu({
  prefix = 'mobile',
  previousHandle,
  response,
  tier = 1,
} = {}) {
  const menu = response?.menu?.items.length ? response.menu.items : response

  return menu.map((object, index) => {
    const handle = previousHandle
      ? `${prefix}-${previousHandle}-${convertToHandle(object.title)}-${tier}-${index}`
      : `${prefix}-${convertToHandle(object.title)}-${tier}-${index}`

    const links = object.items?.length
      ? formatMenu({
        prefix,
        previousHandle: handle,
        response: object.items,
        tier: tier + 1,
      })
      : []

    const urlPath = object.url.split(cnvs.store.localisedPermanentDomain)[1] ||
      object.url.split(cnvs.store.permanentDomain)[1]

    const url = urlPath
      ? `//${location.host}${cnvs.routes.normalisedRoot}${urlPath}`
      : new URL(object.url)?.pathname || object.url

    return {
      handle,
      links,
      tier,
      title: object.title,
      url,
    }
  })
}

/**
 * Format Shopify CDN image URL.
 * @param {String} src - Image URL, can include existing parameters.
 * @param {Object|String} options - Image options object, or image size string.
 * @param {String} options.crop - Crop image positioning when height and width
 * are set.
 * @param {Number} options.height - Height of image in pixels.
 * @param {String} options.pad_color - Background colour to pad image when image
 * cannot be cropped.
 * @param {Number} options.width - Width of image in pixels.
 * @returns {String}
 */
export function formatImageUrl(src, options) {
  if (!isCdnUrl(src)) {
    return src
  }

  const filetype = src.split('.').reverse()[0]?.split('?')[0]
  let version = src.match(/(?!\?|&)(?<version>v=\d+)/gi)

  if (!filetype || !version) {
    return src
  }

  version = version[0]
  const parameters = []

  /**
   * Clean up image URL.
   * - Remove URL parameters.
   * - Standardise protocol.
   * - Remove progressive filetype prefix.
   * - Remove filetype.
   * - Remove sizing, crop, and scale.
   */
  let url = src
    .split('?')[0]
    .split('//')[1]
    .replace('.progressive.', '.')
    .replace(`.${filetype}`, '.')
    .replace(/(?<size>_(?:\d+)?x(?:\d+)?)?(?<crop>_crop_(?:top|center|bottom|left|right))?(?<scale>@(?:2|3)x)?\./g, '.')

  url = `//${url}${filetype}?${version}`

  /**
   * If no options provided then return URL.
   */
  if (!options) {
    return url
  }

  /**
   * Get parameters from options object.
   */
  if (typeof options === 'object') {
    Object.entries(options).forEach(([key, value]) => {
      if (!value || key === 'format') {
        return
      }

      let formattedValue = value

      if (key === 'pad_color') {
        formattedValue = value.replace('#', '')
      }

      parameters.push(`${key}=${formattedValue}`)
    })
  }

  /**
   * Get width and height parameters from options string.
   */
  if (typeof options === 'string') {
    const sizes = options.split('x')

    if (sizes[0]) {
      parameters.push(`width=${sizes[0]}`)
    }

    if (sizes[1]) {
      parameters.push(`height=${sizes[1]}`)
    }

    if (sizes[0] && sizes[1]) {
      parameters.push('crop=center')
    }
  }

  return parameters.length
    ? `${url}&${parameters.join('&')}`
    : url
}

/**
 * Format multi-store countries.
 * @param {Object} countries - Countries storefront API response.
 * @param {Array} instances - Shopify instances..
 * @returns {Object}
 */
export function formatCountries(countries, instances) {
  const formattedCountries = {}

  /**
   * Find instance which has no countries assigned.
   */
  const unassignedInstance = instances.find((instance) => {
    return !instance.countries.length
  })

  /**
   * Format country response.
   */
  countries.localization.availableCountries.forEach((country) => {
    formattedCountries[country.code] = {
      country: {
        code: country.code,
        currency: country.currency?.code,
        currencySymbol: country.currency?.symbol,
        name: country.name,
      },
      defaultLanguage: country.market.metafield?.value,
      instance: formatInstance(unassignedInstance),
      languages: country.availableLanguages.map((language) => {
        return {
          code: language.code.toLowerCase(),
          name: language.name,
        }
      }),
    }
  })

  /**
   * Add instance countries to list if they're not present in list.
   * - Ignores instances with no country codes set.
   */
  instances.forEach((instance) => {
    if (!instance.countries.length) {
      return
    }

    instance.countries.forEach((instanceCountry) => {
      formattedCountries[instanceCountry.code] = {
        ...formattedCountries[instanceCountry.code],
        instance: formatInstance(instance),
      }
    })
  })

  return formattedCountries
}

/**
 * Format TLD instance.
 * @param {Object} instance - Block setting instance object.
 * @returns {Object}
 */
function formatInstance(instance) {

  /**
   * Ensure that URL ends with '/'.
   */
  const formattedMatchedUrl = instance.url.slice(-1) === '/'
    ? instance.url
    : `${instance.url}/`

  /**
   * Set URL to false if current store includes URL.
   * - URL is used to update /localization submit action so set to false if
   *   we want to use the default submit action.
   */
  let url = false
  const originWithoutWww = location.origin.replace('www.', '')

  if (
    !formattedMatchedUrl.includes('localhost') &&
    !formattedMatchedUrl.includes(location.origin) &&
    !formattedMatchedUrl.includes(originWithoutWww)
  ) {
    url = formattedMatchedUrl
  }

  /**
   * Return formatted object.
   */
  return {
    url,
  }
}
